import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  DocumentTextIcon, CheckCircleIcon, XCircleIcon, ClockIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';
import api from '../../api/client';
import { useAuth } from '../../context/AuthContext';

// Director / admin review of doctor's note requests. Approve refunds
// N points to the staff's Points Wallet; deny closes it with a reason.

const fmt = (d) => d ? new Date(d).toLocaleString() : '—';

// DATE-only fields (event_date) come back as UTC-midnight strings that
// render as the previous day in ET. Parse as a calendar date instead.
const fmtDateOnly = (d) => {
  if (!d) return '—';
  const [y, m, day] = String(d).slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, day).toLocaleDateString();
};

export default function DoctorNotesApprovalsPage() {
  const { isDirector } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approveTarget, setApproveTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/doctor-notes');
      setRows(data);
    } catch { toast.error('Could not load requests'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const deny = async (r) => {
    if (!isDirector) return toast.error('Director only.');
    const reason = window.prompt(
      `Deny ${r.first_name} ${r.last_name}'s doctor's note request?\n\nReason (visible to them):`);
    if (!reason || !reason.trim()) return;
    try {
      await api.post(`/doctor-notes/${r.id}/deny`, { decision_notes: reason.trim() });
      toast.success('Denied');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Deny failed');
    }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>;

  const pending = rows.filter(r => r.status === 'pending');
  const history = rows.filter(r => r.status !== 'pending').slice(0, 30);

  return (
    <div className="animate-slide-up max-w-4xl mx-auto">
      <div className="mb-6 flex items-start gap-4">
        <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
          <DocumentTextIcon className="w-6 h-6 text-orange-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Doctor's Note Approvals</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Staff-submitted doctor's notes with point-adjustment requests.
            {pending.length > 0 && <> <strong>{pending.length}</strong> waiting on you.</>}
          </p>
        </div>
      </div>

      <Section title="Pending" empty="No pending requests.">
        {pending.map(r => (
          <Row key={r.id} r={r} tone="yellow">
            <a href={r.doc_url} target="_blank" rel="noreferrer"
              className="text-xs font-medium text-gray-700 hover:text-orange-600 border border-gray-200 hover:border-orange-300 rounded-lg px-3 py-1.5 inline-flex items-center gap-1">
              <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" /> View note
            </a>
            {isDirector && (
              <>
                <button onClick={() => deny(r)}
                  className="text-xs font-medium text-red-700 hover:bg-red-50 border border-red-200 hover:border-red-300 rounded-lg px-3 py-1.5 inline-flex items-center gap-1">
                  <XCircleIcon className="w-3.5 h-3.5" /> Deny
                </button>
                <button onClick={() => setApproveTarget(r)}
                  className="text-xs font-semibold bg-green-500 hover:bg-green-600 text-white rounded-lg px-3 py-1.5 inline-flex items-center gap-1">
                  <CheckCircleIcon className="w-3.5 h-3.5" /> Approve
                </button>
              </>
            )}
          </Row>
        ))}
      </Section>

      <Section title="Recent history" empty="No history yet.">
        {history.map(r => {
          const meta = r.status === 'approved'
            ? { text: 'Approved', cls: 'bg-green-50 text-green-700 border-green-200' }
            : { text: 'Denied',   cls: 'bg-red-50 text-red-700 border-red-200' };
          return (
            <Row key={r.id} r={r} tone={r.status === 'approved' ? 'green' : 'gray'}
                 subline={
                   r.status === 'approved'
                     ? `+${r.points_adjustment} pts refunded — reviewed by ${r.reviewed_by_name} · ${fmt(r.reviewed_at)}`
                     : `Reason: ${r.decision_notes || '—'} · ${fmt(r.reviewed_at)}`
                 }>
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${meta.cls}`}>
                {meta.text}
              </span>
            </Row>
          );
        })}
      </Section>

      {approveTarget && (
        <ApproveModal r={approveTarget}
          onClose={() => setApproveTarget(null)}
          onDone={() => { setApproveTarget(null); load(); }} />
      )}
    </div>
  );
}

function Section({ title, empty, children }) {
  const kids = Array.isArray(children) ? children : [children];
  const hasKids = kids.filter(Boolean).length > 0;
  return (
    <div className="mb-6">
      <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-2">{title}</h2>
      {hasKids ? <div className="space-y-2">{children}</div>
                : <div className="bg-white border border-gray-200 rounded-2xl p-5 text-sm text-gray-500 text-center">{empty}</div>}
    </div>
  );
}

function Row({ r, tone = 'yellow', subline, children }) {
  const borderColor = ({
    yellow: 'border-l-yellow-400', green: 'border-l-green-400', gray: 'border-l-gray-300',
  })[tone] || 'border-l-gray-300';
  return (
    <div className={`bg-white border border-gray-200 rounded-2xl p-4 border-l-4 ${borderColor}`}>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="min-w-0 flex-1">
          <Link to={`/staff/${r.user_id}`} className="text-sm font-semibold text-gray-900 hover:text-orange-600">
            {r.first_name} {r.last_name}
          </Link>
          <p className="text-xs text-gray-500 mt-0.5">
            For {fmtDateOnly(r.event_date)} · Submitted {fmt(r.requested_at)}
          </p>
          {r.staff_message && (
            <p className="text-xs text-gray-500 mt-1 italic">"{r.staff_message}"</p>
          )}
          {subline && <p className="text-[11px] text-gray-400 mt-1">{subline}</p>}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">{children}</div>
      </div>
    </div>
  );
}

function ApproveModal({ r, onClose, onDone }) {
  const [points, setPoints] = useState('5');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const n = parseInt(points, 10);
    if (!Number.isFinite(n) || n <= 0) return toast.error('Points must be a positive number.');
    setBusy(true);
    try {
      await api.post(`/doctor-notes/${r.id}/approve`, {
        points_adjustment: n,
        decision_notes: notes.trim() || null,
      });
      toast.success(`Approved — +${n} refunded to ${r.first_name}'s wallet.`);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Approve failed');
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-base font-semibold text-gray-900 mb-1">Approve — {r.first_name} {r.last_name}</h3>
        <p className="text-xs text-gray-500 mb-3">
          Refunds points to their wallet. Common values: 5 (recovers a call-off-with-note),
          10 (fully cancels a call-off), 5 (offsets a late arrival).
        </p>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-[11px] font-semibold uppercase text-gray-500 block mb-1">Points to refund</label>
            <input type="number" min={1} value={points}
              onChange={(e) => setPoints(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-right" />
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase text-gray-500 block mb-1">Notes (optional)</label>
            <input value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Valid note from Dr. X"
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" />
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose}
              className="text-sm px-3 py-1.5 rounded-lg text-gray-600 hover:bg-gray-100">Cancel</button>
            <button type="submit" disabled={busy}
              className="text-sm px-4 py-1.5 rounded-lg bg-green-600 text-white font-semibold hover:bg-green-700 disabled:opacity-50">
              {busy ? 'Approving…' : `Approve +${points || '0'}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
